<template>
  <div class="change-password-page" v-loader.opacity="loading">
    <div class="container">
      <img src="@/assets/images/phelcom/eyercloud_logo.svg" alt="Eyercloud logo">

      <div class="row align-items-start gx-5">
        <div class="col-lg-6 flex-center">
          <div class="">
            <h1>
              {{ $t('resetPassword.title') }}
            </h1>

            <p>
              {{ $t('changeExpiredPassword.title') }}
            </p>
          </div>
        </div>
        <div class="col-lg-6 flex-center">
          <div>
            <h3 class="mb-3">{{ $t('resetPassword.instructionsMessage') }}</h3>
            <p class="mb-3">{{ $t('resetPassword.instructionsMessagePassword') }}</p>

            <vee-form v-slot="{ handleSubmit }" as="div">
              <form @submit.prevent.stop="handleSubmit(changePassword)">
                <div class="form-floating mb-3">
                  <input type="email" class="form-control" id="email"
                        :placeholder="$t('user.email')"
                        v-model="email"
                        readonly
                        data-test="inputEmailForceChange">
                  <label for="email">{{ $t('user.email') }}</label>
                </div>

                <div class="form-floating mb-3">
                  <vee-field name="currentPassword" v-model="currentPassword" rules="required" v-slot="{ field, errorMessage }">
                    <input v-bind="field" :type="showOldPassword ? 'text' : 'password'" class="form-control" id="currentPassword"
                          :placeholder="$t('user.currentPassword')"
                          data-test="inputCurrentPasswordForceChange"
                          oncopy="return false;" onpaste="return false;" oncut="return false;">
                    <label for="currentPassword">{{ $t('user.currentPassword') }}</label>
                    <button class="btn-icon" type="button" name="button" data-test="btnShowOldPassword" @click="showOldPassword = !showOldPassword">
                      <IconEyePassword v-if="showOldPassword" />
                      <IconShowPassword v-else />
                    </button>
                    <span class="vee-error">{{ errorMessage }}</span>
                  </vee-field>
                </div>

                <div class="form-floating mb-3">
                  <vee-field name="newPassword" v-model="newPassword" rules="required|min:8" v-slot="{ field, errorMessage }">
                    <input v-bind="field" :type="showNewPassword ? 'text' : 'password'" data-test="inputNewPasswordForceChange"
                          new-password class="form-control" ref="newPassword" id="newPassword" :placeholder="$t('user.newPassword')"
                          @keyup="checkPassword(newPassword)" oncopy="return false;" onpaste="return false;" oncut="return false;">
                    <button class="btn-icon" type="button" name="button" data-test="btnShowNewPassword" @click="showNewPassword = !showNewPassword">
                      <IconEyePassword v-if="showNewPassword" />
                      <IconShowPassword v-else />
                    </button>
                    <small class="strength-password" :class="strengthInfo" v-show="strengthInfo">{{ strengthInfo ? $t(`strengthPassword.${strengthInfo}`) : "" }}</small>
                    <span class="vee-error">{{ errorMessage ? errorMessage : $t(`${strengthPasswordMessage}`) }}</span>
                  </vee-field>
                  <label for="newPassword">{{ $t('user.newPassword') }}</label>
                </div>

                <div class="form-floating mb-3">
                  <vee-field name="confirmNewPassword" v-model="confirmNewPassword" rules="required|confirmed:newPassword" v-slot="{ field, errorMessage }">
                    <input v-bind="field" :type="showConfirmNewPassword ? 'text' : 'password'" data-test="inputConfirmNewPasswordForceChange"
                          class="form-control" ref="confirmNewPassword" id="confirmNewPassword" :placeholder="$t('user.passwordConfirmation')"
                          oncopy="return false;" onpaste="return false;" oncut="return false;">
                    <button class="btn-icon" type="button" name="button" data-test="btnShowConfirmNewPassword" @click="showConfirmNewPassword = !showConfirmNewPassword">
                      <IconEyePassword v-if="showConfirmNewPassword" />
                      <IconShowPassword v-else />
                    </button>
                    <span class="vee-error">{{ errorMessage }}</span>
                  </vee-field>
                  <label for="confirmNewPassword">{{ $t('user.passwordConfirmation') }}</label>
                </div>

                <button class="btn btn-success" type="submit" name="button" data-test="btnResetPassword" :disabled="loading">
                  {{ $t('changeExpiredPassword.updatePassword') }}
                </button>
              </form>
            </vee-form>
          </div>
        </div>
      </div>

      <AuthPoweredBy/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .vee-error {
    width:                          100% !important;
  }
  .change-password-page {
    height:                         100vh;
  }

  .container {
    display:                        flex;
    justify-content:                space-between;
    align-items:                    center;
    flex-direction:                 column;
    height:                         100%;
    padding-top:                    2rem;
    padding-bottom:                 1rem;
    > img {
      display:                      block;
      margin-left:                  auto;
      margin-right:                 auto;
    }
  }

  .col-lg-6 {
    display:                        flex;
    justify-content:                flex-start;
    align-items:                    center;
    > div {
      width:                        70%;
    }
    h1 {
      color:                        var(--Brand-Primary, #1B294A);
      font-size:                    38px;
      font-weight:                  300;
      line-height:                  normal;
    }
    &:first-child {
      justify-content:              flex-end;
    }
    &:last-child {
      gap:                          18px;
    }
  }

  .flex-between {
    width:                          40%;
    min-width:                      350px;
    max-width:                      400px;
    gap:                            28px;
    text-align:                     center;
    flex-direction:                 column;

    h1 {
      color:                        var(--Brand-Primary, #1B294A);
      font-size:                    38px;
      font-weight:                  300;
      line-height:                  normal;
    }

    > p {
      color:                        var(--Brand-Primary, #1B294A);
      font-size:                    var(--font-size-14);
      font-weight:                  300;
      line-height:                  140%;
      span {
        font-weight:                700;
      }
    }
  }

  .btn {
    font-weight:                    700;
    letter-spacing:                 4.2px;
    width:                          100%;
    padding:                        20px;
  }

  .strength-password {
    position:                       absolute;
    color:                          white;
    font-size:                      0.7em;
    text-align:                     center;
    height:                         12px;
    width:                          100%;
    top:                            -10px;
    &.danger {
      background:                   $phelcom-red;
    }
    &.warning {
      background:                   $yellow-phelcom;
    }
    &.success {
      background:                   $phelcom-green;
    }
  }

  @media (max-width: 991px) {
    .container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 10px;
    }
    .container > img {
      width:                        160px;
    }
    .col-lg-6 {
      > div {
        width:                      100%;
    }

    .powered-box {
      display:                      flex;
    }
  }
}
</style>

<script>
  import { useRoute, useRouter } from 'vue-router';
  import utilService from '../../services/util-service.js';
  import AuthPoweredBy from '@/views/auth/AuthPoweredBy.vue'

  export default {
    name: 'auth-change-expired-password',
    components: {
      AuthPoweredBy
    },
    data: () => ({
      loading: false,
      isProduction: false,
      alert: {
        message: null,
        class: null,
      },
      email: "",
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: null,
      showOldPassword: false,
      showNewPassword: false,
      showConfirmNewPassword: false,
      countryCode: 'en',
      backgroundStyle: null,
      timezone: null,
      appVersion: process.env.VUE_APP_VERSION,
      strengthPasswordMessage: 'zxcvbnMessages.default',
      strengthInfo: null,
      account: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        pin: "",
      },
    }),
    mounted() {
      this.isProduction = process.env.NODE_ENV != "development"

      this.countryCode = utilService.getCurrentLanguage();

      const imagePath = require(`@/assets/images/phelcom/welcome_banner_${this.countryCode}.png`);
      this.backgroundStyle = `background: url(${imagePath}) no-repeat center bottom 25%, linear-gradient(252deg, #000 0%, #0D4636 100%); background-size: cover;`;

      const route = useRoute();
      this.email = route.query.email || '';

      if (!this.email) {
        useRouter().push('/login');
      }
    },
    watch: {
      newPassword(newValue) {
        this.checkPassword(newValue);
      }
    },
    methods: {
      async checkPassword(password) {
        this.account = {
          firstName: '',
          lastName: '',
          email: this.email,
          password: password,
        };


        let isValidPassword = await utilService.validatePassword(this.account);

        switch (isValidPassword.score) {
          case 0:
          case 1:
          case 2:
            this.strengthInfo = 'danger'
            break;
          case 3:
            this.strengthInfo = 'warning'
            break;
          case 4:
            this.strengthInfo = 'success'
            break;
        }

        if (isValidPassword.valid) {
          this.strengthPasswordMessage = 'zxcvbnMessages.default'
          this.isValidPassword = true
        } else {
          this.strengthPasswordMessage = isValidPassword.warning ? 'zxcvbnMessages.warnings.' + isValidPassword.warning :
                                                              'zxcvbnMessages.suggestions.' + isValidPassword.suggestion[0]
        }
      },
      async changePassword() {
        this.errorMessage = null;
        this.successMessage = null;
        this.loading = true;

        if (this.newPassword !== this.confirmNewPassword) {
          this.errorMessage = this.$t('changeExpiredPassword.mismatchedPasswords');
          this.loading = false;
          return;
        }

        let responseValidate = await utilService.validatePassword(this.account)

        if (responseValidate.valid == false){
          this.$message.popup(this, 'zxcvbnMessages.error', 'operationFail', 'error')
          this.loading = false;
          return;
        }

        try {
          await this.$authService.changeExpiredPassword(
            this.email,
            this.currentPassword,
            this.newPassword
          )

          this.$message.popup(this, 'changeExpiredPassword.passwordChangedSuccess', 'operationFail', 'success')
          setTimeout(() => {
            this.loading = false
            this.$router.push("/login")
          }, 1500)

        } catch (err) {
          if (err.response && err.response.status === 403 && err.response.data && err.response.data.message === 'PASSWORD_NOT_EXPIRED') {
            this.$message.popup(this, 'changeExpiredPassword.passwordNotExpired', 'operationFail', 'error')
            return;
          }
          if (err.response && err.response.status === 500 && err.response.data && err.response.data.message === 'UNEXPECTED_ERROR') {
            this.$message.popup(this, 'changeExpiredPassword.unexpectedError', 'operationFail', 'error')
            return;
          }
          if (err.response && err.response.status === 401 && err.response.data && err.response.data.message === 'INVALID_OLD_PASSWORD') {
            this.$message.popup(this, 'changeExpiredPassword.invalidOldPassword', 'operationFail', 'error')
            return;
          }
          if (err.response && err.response.status === 400 && err.response.data && err.response.data.message === 'NEW_PASSWORD_SAME_AS_OLD') {
            this.$message.popup(this, 'changeExpiredPassword.newPasswordSameAsOld', 'operationFail', 'error')
            return;
          }
          if (err.response && err.response.status === 400 && err.response.data && err.response.data.message === 'PASSWORD_REUSED') {
            this.$message.popup(this, 'changePassword.passwordReused', 'operationFail', 'error')
            return;
          }
          this.$message.popup(this, 'changeExpiredPassword.errorWhenChangingPassword', 'operationFail', 'error')
        } finally {
          this.loading = false;
        }
      }
    }
  }
</script>
